import React, { useState, useEffect, Context, createContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocationInfo } from 'src/hooks';
import { conf } from 'src/config';
import _ from 'lodash';
import * as types from '../types';
import * as selectors from '../selectors';
import * as actions from '../actions';
import * as utils from '../utils';

export type Scope = {
    state: types.Explorer;
    locator: types.Locator;
    entity?: types.Entity;
    morphs?: string[];
    attrNames: string[];
    errors?: types.Error[];
    isAdmin: boolean;
    submit: (onComplete?: types.onCompleteHandler) => void;
    close: (onComplete?: types.onCompleteHandler) => void;
}

export const ExplorerContext: Context<Scope | undefined> = createContext<Scope | undefined>(undefined);

export interface Props {
    children: (props: Scope) => React.ReactElement;
    locator: types.Locator;
}

const Provider: React.FC<Props> = ({ children, locator }) => {
    const dispatch = useDispatch();
    const state: types.Explorer = useSelector(selectors.SelectState());
    const locationInfo = useLocationInfo();

    const [morphs, setMorphs] = useState<string[] | undefined>();
    const [entity, setEntity] = useState<types.Entity>();

    const name: string = entity?.morph ?? locator.name;
    const attrNames = conf.getAttrNames(name, locationInfo.admin && entity?.sys);

    const { errors: allErrors } = entity ?? {};
    const errors: types.Error[] | undefined = _.get(allErrors || {}, "/", undefined);


    useEffect(
        () => {
            const { name, id } = locator;
            const idStr = id.toString();
            if (!utils.isTransient(idStr) && !utils.isTemp(idStr) && !utils.isRoot(idStr)) {
                dispatch(actions.startFetch(locator, true));
            }
            if (conf.isAbstract(name)) {
                setMorphs(conf.getChildMorphs(name));
            } else {
                setMorphs(undefined);
            }
        },
        [locator]
    );

    useEffect(
        () => {
            const { name, id } = locator;
            setEntity(_.get(state.entities, [name, id]));
        },
        [locator, state.entities]
    );

    const submit: (onComplete?: types.onCompleteHandler) => void = (onComplete) => {
        dispatch(actions.submit(locator, onComplete));
    }

    const close: (onComplete?: types.onCompleteHandler) => void = (onComplete) => {
        dispatch(actions.cancel(locator, onComplete));
    }

    const scope = {
        state,
        locator,
        entity,
        morphs,
        attrNames,
        submit,
        close,
        errors,
        isAdmin: locationInfo.admin,
    };

    return <ExplorerContext.Provider value={scope}>
        {children(scope)}
    </ExplorerContext.Provider>
}

export default Provider;
